import { useState, useRef, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { createTradeDB, getStrategiesDB, createStrategyDB, deleteStrategyDB, uploadScreenshot } from "@/lib/supabaseServices";
import GradingCard from "@/components/GradingCard";
import ChartAnnotator from "@/components/ChartAnnotator";
import {
  Upload,
  Loader2,
  Cpu,
  TrendingUp,
  TrendingDown,
  X,
  Bookmark,
  Calculator,
  ShieldAlert,
  Tag,
  Plus,
  Pencil,
} from "lucide-react";

const SESSIONS = ["London", "New York", "Asian"];

const PRESET_TAGS = [
  "Liquidity Sweep",
  "Break of Structure",
  "High Impact News",
  "FOMC",
  "London Killzone",
  "NY Open",
  "Prop Challenge",
];

const field =
  "w-full bg-[#050505] border border-[#1A1A24] px-3 py-2 text-sm text-[#E2E8F0] placeholder:text-[#4a4f59] focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors";
const label = "block text-[11px] tracking-[0.15em] uppercase text-[#848D97] mb-1.5";

const Panel = ({ title, index, color, children }) => (
  <div className="border border-[#1A1A24] bg-[#0C0C0E]">
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[#1A1A24]">
      <span
        className="font-display font-black text-sm w-6 h-6 flex items-center justify-center"
        style={{ color, border: `1px solid ${color}` }}
      >
        {index}
      </span>
      <span className="text-xs tracking-[0.2em] uppercase" style={{ color }}>
        {title}
      </span>
    </div>
    <div className="p-4 space-y-4">{children}</div>
  </div>
);

const empty = {
  date: new Date().toISOString().slice(0, 10),
  session: "London",
  instrument: "",
  direction: "Long",
  htf_tf: "4H",
  exec_tf: "5M",
  indication: "",
  correction: "",
  continuation: "",
  custom_strategy: "",
  entry_price: "",
  stop_loss: "",
  take_profit: "",
  rr_ratio: "",
  mindset: "",
  notes: "",
  tags: [],
};

export default function TradeForm({ onLogged }) {
  const [f, setF] = useState(empty);
  const [accountBalance, setAccountBalance] = useState("10000");
  const [riskPercent, setRiskPercent] = useState("1");
  const [customTagInput, setCustomTagInput] = useState("");

  const [screenshotPath, setScreenshotPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [presets, setPresets] = useState([]);
  const [annotating, setAnnotating] = useState(false);
  const fileRef = useRef(null);

  const handleAnnotatedSave = async (annotatedFile) => {
    setAnnotating(false);
    setUploading(true);
    try {
      const publicUrl = await uploadScreenshot(annotatedFile);
      setScreenshotPath(publicUrl);
      toast.success("Annotated chart saved successfully!");
    } catch (err) {
      toast.error("Failed to save annotated chart");
    } finally {
      setUploading(false);
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const loadPresets = () => getStrategiesDB().then((data) => setPresets(data)).catch(() => {});
  useEffect(() => {
    loadPresets();
  }, []);

  // ─── Calculator Logic ───────────────────────────────────────────────────────
  const calculatedRisk = useMemo(() => {
    const bal = parseFloat(accountBalance);
    const pct = parseFloat(riskPercent);
    if (!isNaN(bal) && !isNaN(pct) && bal > 0 && pct > 0) {
      const dollarRisk = (bal * pct) / 100;
      let lotSize = null;
      const entry = parseFloat(f.entry_price);
      const sl = parseFloat(f.stop_loss);
      if (!isNaN(entry) && !isNaN(sl) && entry > 0 && sl > 0) {
        const pipsDiff = Math.abs(entry - sl) * 10000;
        if (pipsDiff > 0) {
          lotSize = (dollarRisk / (pipsDiff * 10)).toFixed(2);
        }
      }
      return { dollarRisk: dollarRisk.toFixed(2), lotSize };
    }
    return null;
  }, [accountBalance, riskPercent, f.entry_price, f.stop_loss]);

  // Auto-calc R:R when entry/SL/TP change
  useEffect(() => {
    const entry = parseFloat(f.entry_price);
    const sl = parseFloat(f.stop_loss);
    const tp = parseFloat(f.take_profit);
    if (!isNaN(entry) && !isNaN(sl) && !isNaN(tp)) {
      const risk = Math.abs(entry - sl);
      const reward = Math.abs(tp - entry);
      if (risk > 0) {
        const rr = (reward / risk).toFixed(2);
        setF((prev) => ({ ...prev, rr_ratio: rr }));
      }
    }
  }, [f.entry_price, f.stop_loss, f.take_profit]);

  // Live Rule Enforcer
  const warnings = useMemo(() => {
    const w = [];
    const rr = parseFloat(f.rr_ratio);
    if (!isNaN(rr) && rr < 2.0) {
      w.push("R:R is below 1:2 — ICC rules require at least 1:2 RR");
    }
    if (f.indication && f.indication.toLowerCase().includes("pending")) {
      w.push("Indication is pending — wait for HTF break before logging");
    }
    return w;
  }, [f.rr_ratio, f.indication]);

  const [customTags, setCustomTags] = useState([]);

  const toggleTag = (tag) => {
    const current = f.tags || [];
    if (current.includes(tag)) {
      setF({ ...f, tags: current.filter((t) => t !== tag) });
    } else {
      if (current.length >= 6) {
        toast.error("Maximum 6 tags allowed per trade");
        return;
      }
      setF({ ...f, tags: [...current, tag] });
    }
  };

  const removeTag = (tag) => {
    const current = f.tags || [];
    setF({ ...f, tags: current.filter((t) => t !== tag) });
  };

  const addCustomTag = (e) => {
    if (e) e.preventDefault();
    const tag = customTagInput.trim();
    if (!tag) return;
    const current = f.tags || [];
    if (current.length >= 6 && !current.includes(tag)) {
      toast.error("Maximum 6 tags allowed per trade");
      return;
    }
    if (!customTags.includes(tag) && !PRESET_TAGS.includes(tag)) {
      setCustomTags((prev) => [...prev, tag]);
    }
    if (!current.includes(tag)) {
      setF({ ...f, tags: [...current, tag] });
      toast.success(`Tag "${tag}" added`);
    }
    setCustomTagInput("");
  };

  const saveCurrentStrategy = async () => {
    const name = f.custom_strategy.trim();
    if (!name) {
      toast.error("Type a strategy first");
      return;
    }
    try {
      await createStrategyDB(name);
      toast.success("Strategy saved");
      loadPresets();
    } catch (err) {
      toast.error(err?.message || "Failed to save strategy");
    }
  };

  const deletePreset = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteStrategyDB(id);
      loadPresets();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const publicUrl = await uploadScreenshot(file);
      setScreenshotPath(publicUrl);
      toast.success("Chart uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!f.instrument || !f.indication || !f.correction || !f.continuation) {
      toast.error("Fill instrument and all 3 ICC phases");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        ...f,
        entry_price: f.entry_price ? parseFloat(f.entry_price) : null,
        stop_loss: f.stop_loss ? parseFloat(f.stop_loss) : null,
        take_profit: f.take_profit ? parseFloat(f.take_profit) : null,
        rr_ratio: f.rr_ratio ? parseFloat(f.rr_ratio) : null,
        screenshot_path: screenshotPath,
        outcome: f.outcome || "pending",
      };
      const created = await createTradeDB(payload);
      // Reset form input values so user cannot accidentally submit duplicate
      setF(empty);
      setScreenshotPath(null);
      if (fileRef.current) fileRef.current.value = "";

      if (created.grading) {
        setResult(created.grading);
        toast.success(`Trade graded: ${created.grading.icc_score}`);
        onLogged();
      } else {
        toast.success("Trade saved successfully!");
        onLogged();
      }
    } catch (err) {
      toast.error(err?.message || "Failed to save trade");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setF(empty);
    setScreenshotPath(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-white leading-none">
            LOG A <span className="text-[#00F0FF] text-glow-cyan">TRADE</span>
          </h1>
          <p className="text-[#848D97] text-sm mt-2">
            Break it down by phase. The AI coach grades your ICC sequence, strategy &amp; risk.
          </p>
        </div>
      </div>

      {/* 🚨 Rule Enforcer Live Warnings */}
      <AnimatePresence>
        {warnings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="border border-[#FF003C] bg-[#FF003C]/10 p-4 space-y-1.5 text-xs text-[#FF003C]"
            data-testid="rule-warnings"
          >
            <div className="flex items-center gap-2 font-bold uppercase tracking-wider mb-1">
              <ShieldAlert className="w-4 h-4 text-[#FF003C]" /> Mechanical Rule Warnings
            </div>
            {warnings.map((w, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span>•</span>
                <span>{w}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <form onSubmit={submit} className="space-y-6" data-testid="trade-form">
        {/* Meta row */}
        <div className="border border-[#1A1A24] bg-[#0C0C0E] p-4 grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <label className={label}>Date</label>
            <input
              type="date"
              value={f.date}
              onChange={set("date")}
              className={field}
              style={{ colorScheme: "dark" }}
              data-testid="input-date"
            />
          </div>
          <div>
            <label className={label}>Session</label>
            <select value={f.session} onChange={set("session")} className={field} data-testid="input-session">
              {SESSIONS.map((s) => (
                <option key={s} value={s} className="bg-[#0C0C0E]">
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={label}>Instrument</label>
            <input
              value={f.instrument}
              onChange={set("instrument")}
              placeholder="XAUUSD"
              className={field}
              data-testid="input-instrument"
            />
          </div>
          <div>
            <label className={label}>Direction</label>
            <div className="grid grid-cols-2 gap-1">
              {["Long", "Short"].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setF({ ...f, direction: d })}
                  data-testid={`direction-${d.toLowerCase()}`}
                  className={`py-2 text-xs flex items-center justify-center gap-1 border transition-colors ${
                    f.direction === d
                      ? d === "Long"
                        ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5"
                        : "border-[#FF003C] text-[#FF003C] bg-[#FF003C]/5"
                      : "border-[#1A1A24] text-[#848D97]"
                  }`}
                >
                  {d === "Long" ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={label}>HTF Bias</label>
            <input value={f.htf_tf} onChange={set("htf_tf")} placeholder="4H" className={field} data-testid="input-htf" />
          </div>
          <div>
            <label className={label}>Execution TF</label>
            <input value={f.exec_tf} onChange={set("exec_tf")} placeholder="5M" className={field} data-testid="input-exec" />
          </div>
          <div>
            <label className={label}>Outcome</label>
            <select value={f.outcome || "pending"} onChange={set("outcome")} className={field} data-testid="input-outcome">
              <option value="pending" className="bg-[#0C0C0E]">Pending</option>
              <option value="win" className="bg-[#0C0C0E] text-[#00FF41]">Win ✓</option>
              <option value="loss" className="bg-[#0C0C0E] text-[#FF003C]">Loss ✗</option>
            </select>
          </div>
        </div>

        {/* 3 ICC phases */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Panel index="1" title="Indication" color="#00F0FF">
            <div>
              <label className={label}>Which swing broke on HTF?</label>
              <textarea
                rows={4}
                value={f.indication}
                onChange={set("indication")}
                placeholder="Clean break of 4H swing high → bullish institutional intent..."
                className={field}
                data-testid="input-indication"
              />
            </div>
          </Panel>
          <Panel index="2" title="Correction" color="#FFD700">
            <div>
              <label className={label}>Pullback depth &amp; behavior</label>
              <textarea
                rows={4}
                value={f.correction}
                onChange={set("correction")}
                placeholder="Retraced to 61.8%, printed lower highs trapping retail longs..."
                className={field}
                data-testid="input-correction"
              />
            </div>
          </Panel>
          <Panel index="3" title="Continuation" color="#00FF41">
            <div>
              <label className={label}>LTF confirmation / structure break</label>
              <textarea
                rows={4}
                value={f.continuation}
                onChange={set("continuation")}
                placeholder="5M broke back above correction high with strong bullish engulfing..."
                className={field}
                data-testid="input-continuation"
              />
            </div>
          </Panel>
        </div>

        {/* Market Condition Tags */}
        <div className="border border-[#1A1A24] bg-[#0C0C0E] p-4 space-y-4">
          <div className="flex items-center justify-between">
            <label className={`${label} flex items-center gap-1.5 mb-0`}>
              <Tag className="w-3.5 h-3.5 text-[#00F0FF]" /> Market Condition Tags
            </label>
            <span className={`text-[10px] font-mono font-bold ${
              (f.tags || []).length >= 6 ? "text-[#FF003C]" : "text-[#00F0FF]"
            }`}>
              {(f.tags || []).length}/6 Selected
            </span>
          </div>

          {/* Active Selected Tags (with remove button) */}
          {(f.tags || []).length > 0 && (
            <div className="p-2.5 border border-[#00F0FF]/20 bg-[#050505] space-y-1.5">
              <span className="text-[10px] text-[#848D97] uppercase tracking-wider block">Active Tags:</span>
              <div className="flex flex-wrap gap-1.5">
                {f.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-mono font-bold bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/40 rounded-sm"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTag(t)}
                      className="hover:text-[#FF003C] transition-colors ml-0.5"
                      title="Remove tag"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* All Available Tags (Presets + Custom) */}
          <div className="space-y-1.5">
            <span className="text-[10px] text-[#848D97] uppercase tracking-wider block">Available Tags:</span>
            <div className="flex flex-wrap gap-2">
              {[...PRESET_TAGS, ...customTags.filter((ct) => !PRESET_TAGS.includes(ct))].map((t) => {
                const active = (f.tags || []).includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    className={`px-2.5 py-1 text-xs border transition-all ${
                      active
                        ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10 font-bold"
                        : "border-[#1A1A24] text-[#848D97] hover:border-[#00F0FF]/40 hover:text-white bg-[#050505]"
                    }`}
                  >
                    {active ? `✓ ${t}` : `+ ${t}`}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom tag add form */}
          <form onSubmit={addCustomTag} className="flex gap-2 max-w-sm pt-1">
            <input
              value={customTagInput}
              onChange={(e) => setCustomTagInput(e.target.value)}
              placeholder="Add custom tag..."
              className={field}
            />
            <button
              type="submit"
              disabled={(f.tags || []).length >= 6}
              className="px-3 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 text-xs flex-shrink-0"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </form>
        </div>

        {/* Strategy + Risk */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-[#1A1A24] bg-[#0C0C0E] p-4 space-y-4">
            <div>
              <label className={label}>Custom Entry Strategy Tag</label>
              <div className="flex gap-2">
                <input
                  value={f.custom_strategy}
                  onChange={set("custom_strategy")}
                  placeholder="EMA 21 cross + London killzone + engulfing"
                  className={field}
                  data-testid="input-strategy"
                />
                <button
                  type="button"
                  onClick={saveCurrentStrategy}
                  title="Save strategy preset"
                  data-testid="save-preset"
                  className="flex-shrink-0 px-3 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors flex items-center gap-1 text-xs"
                >
                  <Bookmark className="w-4 h-4" />
                </button>
              </div>
              {presets.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="preset-chips">
                  {presets.map((p) => {
                    const selected = f.custom_strategy === p.name;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setF({ ...f, custom_strategy: selected ? "" : p.name })}
                        data-testid={`preset-${p.id}`}
                        className={`group flex items-center gap-1.5 px-2.5 py-1 text-[11px] border transition-colors ${
                          selected
                            ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10 font-bold"
                            : "border-[#1A1A24] text-[#848D97] hover:border-[#00F0FF]/50 hover:text-[#E2E8F0]"
                        }`}
                      >
                        {p.name}
                        <X
                          className="w-3 h-3 opacity-60 group-hover:opacity-100 hover:text-[#FF003C] transition-opacity"
                          onClick={(e) => deletePreset(p.id, e)}
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label className={label}>Mindset / Emotional State</label>
              <input
                value={f.mindset}
                onChange={set("mindset")}
                placeholder="Patient, waited for confirmation"
                className={field}
                data-testid="input-mindset"
              />
            </div>
            <div>
              <label className={label}>Notes</label>
              <textarea rows={2} value={f.notes} onChange={set("notes")} className={field} data-testid="input-notes" />
            </div>
          </div>

          <div className="border border-[#1A1A24] bg-[#0C0C0E] p-4 space-y-4">
            {/* Position Size & Risk Calculator Widget */}
            <div className="border border-[#00F0FF]/20 bg-[#050505] p-3 text-xs space-y-2">
              <div className="flex items-center justify-between text-[#00F0FF] uppercase tracking-wider font-bold">
                <span className="flex items-center gap-1">
                  <Calculator className="w-3.5 h-3.5" /> Position Risk Calculator
                </span>
                {calculatedRisk?.dollarRisk && (
                  <span className="text-[#00FF41]">Risk: ${calculatedRisk.dollarRisk}</span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-[#848D97]">Account Balance ($)</label>
                  <input
                    type="number"
                    value={accountBalance}
                    onChange={(e) => setAccountBalance(e.target.value)}
                    className={field}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-[#848D97]">Risk Per Trade (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={riskPercent}
                    onChange={(e) => setRiskPercent(e.target.value)}
                    className={field}
                  />
                </div>
              </div>
              {calculatedRisk?.lotSize && (
                <div className="pt-1 text-[#FFD700] text-[11px] font-mono flex items-center justify-between border-t border-[#1A1A24]">
                  <span>Calculated Position Size:</span>
                  <span className="font-bold text-white text-xs">{calculatedRisk.lotSize} Lots</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>Entry Price</label>
                <input value={f.entry_price} onChange={set("entry_price")} className={field} data-testid="input-entry" />
              </div>
              <div>
                <label className={label}>Stop Loss</label>
                <input value={f.stop_loss} onChange={set("stop_loss")} className={field} data-testid="input-sl" />
              </div>
              <div>
                <label className={label}>Take Profit</label>
                <input value={f.take_profit} onChange={set("take_profit")} className={field} data-testid="input-tp" />
              </div>
              <div>
                <label className={label}>R:R Ratio</label>
                <input value={f.rr_ratio} onChange={set("rr_ratio")} placeholder="2.5" className={field} data-testid="input-rr" />
              </div>
            </div>

            <div>
              <label className={label}>Chart Screenshot</label>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="input-file" />
              {screenshotPath ? (
                <div className="relative border border-[#00F0FF]/40 bg-[#050505]">
                  <img src={fileUrl(screenshotPath)} alt="chart" className="w-full max-h-48 object-cover" />
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => setAnnotating(true)}
                      className="bg-black/80 hover:bg-black border border-[#00F0FF] px-2 py-1 text-[#00F0FF] rounded text-xs flex items-center gap-1 shadow-lg"
                      title="Annotate Indication, Correction, Continuation"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                      Annotate
                    </button>
                    <button
                      type="button"
                      onClick={() => setScreenshotPath(null)}
                      className="bg-black/80 hover:bg-black border border-[#FF003C] p-1 text-[#FF003C] rounded"
                      data-testid="remove-screenshot"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-btn"
                  className="w-full border border-dashed border-[#1A1A24] hover:border-[#00F0FF] py-6 flex flex-col items-center gap-2 text-[#848D97] hover:text-[#00F0FF] transition-colors"
                >
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <span className="text-xs">{uploading ? "Uploading..." : "Upload chart"}</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {annotating && screenshotPath && (
          <ChartAnnotator
            imageSrc={fileUrl(screenshotPath)}
            onSave={handleAnnotatedSave}
            onClose={() => setAnnotating(false)}
          />
        )}

        <div className="flex items-center gap-3">
          <motion.button
            whileTap={{ scale: 0.97 }}
            type="submit"
            disabled={loading}
            data-testid="submit-trade"
            className="flex items-center gap-2 px-8 py-3 bg-[#00F0FF] text-black font-bold text-sm uppercase tracking-wider glow-cyan disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cpu className="w-4 h-4" />}
            {loading ? "AI grading..." : "Grade my setup"}
          </motion.button>
          <button
            type="button"
            onClick={reset}
            data-testid="reset-form"
            className="px-5 py-3 border border-[#1A1A24] text-[#848D97] hover:text-white text-sm uppercase tracking-wider"
          >
            Reset
          </button>
        </div>
      </form>

      {result && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-lg text-white">// AI COACH VERDICT</h2>
            <button
              onClick={onLogged}
              data-testid="view-in-history"
              className="text-xs text-[#00F0FF] border border-[#00F0FF]/40 px-3 py-1.5 hover:bg-[#00F0FF]/10"
            >
              Saved → View history
            </button>
          </div>
          <GradingCard grading={result} />
        </div>
      )}
    </div>
  );
}
